import { endpoints } from '@rossum/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { isDeepEqual } from 'remeda';
import { QUERY_KEY_HOOKS } from '../../../../../../../business/hooks/useHooks';
import { useApiClient } from '../../../../../../../lib/apiClient';
import { assertNever } from '../../../../../../../lib/typeUtils';
import { notify } from '../../../../../../toaster';
import {
  ExternalHookInterface,
  externalHookTemplateSchema,
} from '../customExtensionSchema';
import { toUpstreamMetadata } from '../upstreamSchema';
import { makeOperations } from './makeOperations';

// Hook integrations call the same code iff their type and payload is the same.
// Luckily, there is no camelcase attribute, so this accepts also HookInterface
const isFunctionallyEqual = (
  a: ExternalHookInterface,
  b: ExternalHookInterface
) => {
  return a.type === b.type && isDeepEqual(a.payload, b.payload);
};

export const useUpdate = ({ hookId }: { hookId: number }) => {
  const intl = useIntl();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      version,
      externalHookTemplateId,
      hookInterfaceId,
    }: {
      version: string;
      externalHookTemplateId: string;
      hookInterfaceId: number;
    }) =>
      apiClient
        .request(
          endpoints.hookInterfaces.invoke(hookInterfaceId, {
            payload: {
              name: 'checkout_hook_template',
              id: externalHookTemplateId,
              version,
            },
          })
        )
        .then(hookTemplate => externalHookTemplateSchema.parse(hookTemplate))
        .then(async hookTemplate => {
          // Update hook
          const updatedHook = await apiClient.request(
            endpoints.hooks.patch(hookId, {
              type: hookTemplate.type,
              description: hookTemplate.description,
              name: hookTemplate.name,
              events: hookTemplate.events,
              config: {
                ...hookTemplate.config,
                code: hookTemplate.config.code ?? '',
              },
              metadata: {
                upstream: toUpstreamMetadata({
                  hookInterfaceId,
                  externalHookTemplateId,
                  version,
                }),
              },
            })
          );

          // Get all previous hook integrations
          const currentHookInterfaces = await apiClient.request(
            endpoints.hookInterfaces.list({ hook: updatedHook.id })
          );

          // Get operations that should be performed
          const operations = makeOperations(
            currentHookInterfaces.results,
            hookTemplate.hook_interfaces,
            isFunctionallyEqual
          );

          // Do them one by one
          await Promise.all(
            operations.map(op => {
              if (op.type === 'patch')
                return apiClient.request(
                  endpoints.hookInterfaces.patch(op.current.id, op.upstream)
                );

              if (op.type === 'delete') {
                return apiClient.request(
                  endpoints.hookInterfaces.delete(op.current.id)
                );
              }

              if (op.type === 'create') {
                return apiClient.request(
                  endpoints.hookInterfaces.create({
                    ...op.upstream,
                    hook: updatedHook.url,
                  })
                );
              }

              return assertNever(op);
            })
          );

          return updatedHook;
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_HOOKS, 'detail', hookId],
      });
      notify.success({
        title: intl.formatMessage({
          id: 'features.externalStore.message.update',
        }),
      });
    },
    onError: () => {
      notify.error({
        title: intl.formatMessage({
          id: 'features.externalStore.message.error',
        }),
      });
    },
  });
};
